import { Link, useLocation, matchPath } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { NavItemType } from 'types/menu';
import menuItems from 'menu-items';
import { navAccentSx, navItemSx } from '../../sidebarStyles';

// Todas as urls do menu (coletadas uma vez) — base do "match mais específico vence":
// um item só fica ativo se nenhuma sub-rota dele (outro item de menu) também casar.
const coletarUrls = (itens: NavItemType[], acc: string[] = []): string[] => {
  itens.forEach((it) => {
    if (it.url) acc.push(it.url);
    if (it.children) coletarUrls(it.children, acc);
  });
  return acc;
};
const TODAS_URLS = coletarUrls(menuItems.items as NavItemType[]);

type NavItemProps = {
  item: NavItemType;
  level: number;
};

export default function NavItem({ item, level }: NavItemProps) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  // Item externo → <a>; interno → react-router Link. Abrir em nova aba sempre com rel seguro.
  const openInNewTab = !!item.external || !!item.target;

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
  };

  // Linha em layout expandido: drawer aberto OU item aninhado (nível > 1, sempre com rótulo — ex.: flyout do submenu).
  const rowOpen = drawerOpen || level !== 1;

  const Icon = item.icon;
  const itemIcon = Icon ? <Icon style={{ fontSize: rowOpen ? '1.125rem' : '1.25rem' }} /> : null;

  const { pathname } = useLocation();
  const casa = (u: string) => !!u && !!matchPath({ path: u, end: false }, pathname);
  const url = item.url ?? '';
  // Ativo se este item casa E nenhuma sub-rota dele (outro item de menu) casar — evita
  // que /projetos acenda junto com /projetos/minhas-tarefas (bug de match por prefixo).
  const isSelected = casa(url) && !TODAS_URLS.some((u) => u !== url && u.startsWith(`${url}/`) && casa(u));

  // Tooltip só quando recolhido no nível 1 (ícone sem rótulo).
  const showTooltip = !drawerOpen && level === 1;

  const button = (
    <ListItemButton
      component={item.external ? 'a' : Link}
      {...(item.external ? { href: item.url ?? '#' } : { to: item.url ?? '#' })}
      {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      disabled={item.disabled}
      aria-current={isSelected ? 'page' : undefined}
      onClick={itemHandler}
      sx={(theme) => navItemSx(theme, { open: rowOpen, level, active: isSelected })}
    >
      {itemIcon && <ListItemIcon sx={{ minWidth: rowOpen ? 32 : 0, justifyContent: 'center' }}>{itemIcon}</ListItemIcon>}
      {rowOpen && (
        <ListItemText
          primary={
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: isSelected ? 600 : 500, color: 'text.primary' }}>{item.title}</Typography>
          }
        />
      )}
    </ListItemButton>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {isSelected && <Box aria-hidden sx={(theme) => navAccentSx(theme, rowOpen)} />}
      {showTooltip ? (
        <Tooltip title={item.title} placement="right" arrow disableInteractive>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </Box>
  );
}
